/**
 * The moon: one bright disc and one soft halo, hung out along the line the light comes
 * from, on outdoor levels only.
 *
 * It exists because a night lit by a single hard directional light and nothing in the sky
 * is a night with a hole in it. Every hedge in the maze throws a shadow, all of them
 * parallel, all of them pointing the same way — and the player who follows them back finds
 * an empty sky. So the moon is placed by the *same* function that places the sun
 * (`sunDirection`, in the pure lighting module), not by an authored angle that happens to
 * look about right: the two cannot drift apart, because there is only one number.
 *
 * **World-fixed, never parented to the camera.** That is the same decision `ExteriorWorld`
 * makes and for the same reason — a moon that follows the eye is in front of whichever way
 * you turn, which reads as a smear on the lens rather than a body in the sky. Fixed, it
 * stays where it was when you look back, which is the visual-style requirement in as many
 * words. The price is that it is a real object at a real distance rather than one at
 * infinity, so crossing the grounds does change how big it looks; it is placed far enough
 * out that the change is a slow drift rather than a zoom.
 *
 * Scenery in the strictest sense: no collider, no light of its own — the sun is the light,
 * this is only where the light says it is coming from — and nothing cast or received.
 *
 * Two draw calls, no per-frame work, no state.
 */

import { useMemo } from 'react';
import { CanvasTexture, SRGBColorSpace } from 'three';
import { buildExterior } from '../game/profile/buildExterior';
import type { BuiltLevel } from '../game/profile/buildGeometry';
import { sunDirection } from '../game/profile/buildLighting';
import { moonHaloCanvas } from './textures';
import { useDisposable } from './useDisposable';

type Props = {
  built: BuiltLevel;
};

/**
 * How far out it hangs, as a fraction of the backdrop radius.
 *
 * Two edges to stay inside. The backdrop sphere is opaque and writes depth, so anything at
 * or beyond its radius is simply not drawn; and the near woods hang on their own sphere
 * inside that, painted opaque wherever a tree is. Comfortably inside both puts the moon in
 * front of the sky it is meant to be in, and as far from the player as that allows — which
 * is what keeps a world-fixed body from swelling as they walk toward it.
 */
const DISTANCE_FRACTION = 0.88;

/**
 * Disc and halo radii, as fractions of that distance — so the moon subtends the same angle
 * from the middle of the level whatever size the level is.
 *
 * Generously larger than the real one, which is half a degree and would be four pixels of
 * pale grey. This is about four degrees across, in the register the rest of the art is
 * drawn in: a moon you can find by looking, not one you have to hunt for.
 */
const DISC_FRACTION = 0.035;
const HALO_FRACTION = 0.11;

/** Cold and pale, a shade brighter than the light it stands for (`#9aabc4`). */
const MOON_COLOR = '#e4ecfb';

export function Moon({ built }: Props) {
  const { center, backdropRadius } = buildExterior(built);
  const direction = sunDirection(built);

  // A GPU resource, so it has to be the same object across renders — the same reason
  // `ExteriorWorld` memoizes its canvases. There is one moon, so the canvas itself is
  // cached in `textures` and this only wraps it.
  const halo = useMemo(() => {
    const canvas = moonHaloCanvas();
    if (!canvas) return null;
    const texture = new CanvasTexture(canvas);
    texture.colorSpace = SRGBColorSpace;
    return texture;
  }, []);
  useDisposable(halo);

  const distance = backdropRadius * DISTANCE_FRACTION;

  // Aim the halo back at the middle of the world. The plane's own normal is +z, so a yaw on
  // the outer group and a pitch on the mesh compose to turn it through -direction: the yaw
  // swings it round the horizon, the pitch tips it down from the sky. (One axis each, so
  // Euler order never comes into it.) The player is never more than a fraction of this
  // distance off centre, so the few degrees they are off axis foreshorten a soft round glow
  // by nothing anyone can see.
  const yaw = Math.atan2(-direction.x, -direction.z);
  const pitch = Math.asin(direction.y);

  return (
    <group
      position={[
        center.x + direction.x * distance,
        direction.y * distance,
        center.z + direction.z * distance,
      ]}
      rotation={[0, yaw, 0]}
    >
      {/* A sphere, not a disc, and this is the trick that saves a billboard: an unlit
          material shades every fragment the same colour, so a sphere of it is a perfect
          flat circle from every angle without anything having to turn to face the camera.
          `meshBasicMaterial` for the other reason too — the moon is the light source in the
          fiction, and a lit material would have the sun shading its own moon. */}
      <mesh>
        <sphereGeometry args={[distance * DISC_FRACTION, 20, 12]} />
        {/* Fog would grey it out at this distance, and a fogged moon is a smudge. */}
        <meshBasicMaterial color={MOON_COLOR} fog={false} />
      </mesh>

      {halo && (
        <mesh
          rotation={[pitch, 0, 0]}
          // Past every other transparent shell out here. `ExteriorWorld` sorts far rain 1,
          // near woods 2, near rain 3; the moon is nearer than any of them, and left to the
          // renderer's back-to-front sort it would be drawn first and painted over — these
          // all disable depth writes, so depth cannot settle it for them.
          renderOrder={4}
        >
          <circleGeometry args={[distance * HALO_FRACTION, 24]} />
          <meshBasicMaterial map={halo} transparent depthWrite={false} fog={false} />
        </mesh>
      )}
    </group>
  );
}
